import axios from 'axios';
import { ping } from '@phoenix/components/Ping';
import { emitter, CART_RESTARTED } from '@phoenix/event-bus';

let instance = null;

class ClientApi {
  constructor() {
    // Create Axios HTTP client
    this.client = axios.create();

    this.defaultConfig = {};

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 419 && !originalRequest._retry) {
          originalRequest._retry = true;

          await ping.setShouldRefreshPing();

          return this.client(originalRequest);
        }

        if (error.response?.data?.cart_restarted) {
          if (error.response.data.cart_products_removed) {
            window.Phoenix.toaster.notify({
              type: 'info',
              text: window.Phoenix.i18n.t('Checkout.cartCancelled'),
            });
          }

          emitter.emit(CART_RESTARTED);
        }

        return Promise.reject(error);
      },
    );

    this.client.interceptors.response.use((response) => {
      const { data } = response;
      if (data?.data?.pingResult) {
        ping.updateLocalStorage(data.data.pingResult);
      }

      if (Array.isArray(data?.notifications) && data.notifications.length > 0) {
        window.Phoenix?.notification?.push?.(data.notifications);
      }

      return response;
    });

    this.client.interceptors.request.use(async (config) => {
      const token = await ping.getCsrfToken();
      config.headers['X-CSRF-TOKEN'] = token;
      return config;
    });
  }

  static getInstance() {
    if (instance === null) {
      instance = new ClientApi();
    }

    return instance;
  }

  getConfig(config = null) {
    return { ...this.defaultConfig, ...config };
  }

  request(config = null) {
    return this.client.request(this.getConfig(config));
  }

  get(url, config = null) {
    return this.client.get(url, this.getConfig(config));
  }

  post(url, data = null, config = null) {
    return this.client.post(url, data, this.getConfig(config));
  }

  put(url, data = null, config = null) {
    return this.client.put(url, data, this.getConfig(config));
  }

  patch(url, data = null, config = null) {
    return this.client.patch(url, data, this.getConfig(config));
  }

  delete(url, config = null) {
    return this.client.delete(url, this.getConfig(config));
  }

  options(url, config = null) {
    return this.client.options(url, this.getConfig(config));
  }

  /**
   * @deprecated Deprecated since Axios 0.22.0 (https://axios-http.com/docs/cancellation)
   */
  getCancelToken() {
    return axios.CancelToken;
  }

  isCancel(error) {
    return axios.isCancel(error);
  }
}

export const clientApi = ClientApi.getInstance();
